import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { FormProvider, useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Card, CircularProgress, Stack, Typography, IconButton } from "@mui/material"
import { toast } from "react-toastify"
import { FormBlock } from "../../../../../common/FormGenerator"
import { getDynamicConfig } from "./settingsConfig"
import { whoAvailableSchema } from "../ZodValidationSchema/WhoAvailableSchema"
import { useGetProjectWhoAvailableQuery, useUpdateProjectWhoAvailableMutation } from "../../../../../api/projects"
import { CardStyle } from "./style"
import { IconCloseRed } from "../../../../../icons/IconCloseRed"

export const EditWhoAvailableForm = () => {
    const { id } = useParams<{ id: string }>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const projectId = id ? parseInt(id, 10) : NaN
    const {
        data: loaded,
        isLoading,
        isError,
    } = useGetProjectWhoAvailableQuery(
        { projectId },
        { skip: Number.isNaN(projectId) }
    )
    const [updateWhoAvailable] = useUpdateProjectWhoAvailableMutation()

    const methods = useForm({
        mode: 'onBlur',
        resolver: zodResolver(whoAvailableSchema),
        defaultValues: {
            whoAvailable: [{ title: "", description: "" }],
        },
    })

    const { control, handleSubmit, reset } = methods
    const { fields, append, remove } = useFieldArray({
        control,
        name: "whoAvailable",
    })

    useEffect(() => {
        if (loaded) {
            reset(loaded)
        }
    }, [loaded, reset])

    useEffect(() => {
        if (isError) {
            toast.error("Не удалось загрузить данные кто уже есть")
        }
    }, [isError])

    const addItem = () => {
        append({
            title: "",
            description: "",
        })
    }

    const onSubmit = async (data: Record<string, unknown>) => {
        if (Number.isNaN(projectId)) return

        setIsSubmitting(true)
        try {
            await updateWhoAvailable({ projectId, body: data }).unwrap()
            toast.success("Данные 'Кто уже есть' сохранены")
        } catch {
            toast.error("Не удалось сохранить данные 'Кто уже есть'")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
            </Stack>
        )
    }

    return (
        <FormProvider {...methods}>
            <Stack gap={2} component="form" onSubmit={handleSubmit(onSubmit)} height="100%">
                {fields.length === 0 ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '60vh' }}
                    >
                        <Typography variant="black-16-semiBold">
                            Список "кто уже есть" пуст
                        </Typography>
                    </Stack>
                ) : (
                    fields.map((field, index) => (
                        <Card key={field.id} variant="primaryCard" sx={CardStyle}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography
                                    variant="black-16-semiBold"
                                    component="div"
                                >
                                    Название
                                </Typography>
                                <IconButton
                                    onClick={() => remove(index)}
                                    size="small"
                                    sx={{ p: 0, my: -0.5, }}
                                >
                                    <IconCloseRed fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Box mb={1.5}>
                                <FormBlock formFieldConfig={getDynamicConfig('titleWhoAvailable', index, "whoAvailable")} />
                            </Box>
                            <Typography
                                variant="black-16-semiBold"
                                component="div"
                                mb={1}
                            >
                                Описание
                            </Typography>
                            <FormBlock formFieldConfig={getDynamicConfig('descriptionWhoAvailable', index, "whoAvailable")} />
                        </Card>
                    ))
                )}
                <Stack direction="row" spacing={1}>
                    <Button
                        type="button"
                        variant="action"
                        onClick={addItem}
                    >
                        Добавить
                    </Button>
                

                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="follow"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={16} color="inherit" />
                                    <span>Сохранение...</span>
                                </Stack>
                            ) : (
                                "Сохранить"
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </FormProvider>
    )
}